import { useQuery } from '@tanstack/react-query';
import { supabaseCoreClient } from '../supabase.ts';
import { commonQueryProps } from './cache.js';
import type { GmailMailbox, GmailMetadata, GmailProvider } from '../../types/metadata.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

export const useGmailMetadata = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const bootReady = useMetadataBootReady();
  return useQuery<GmailMetadata, Error>({
    queryKey: ['metadata', 'gmail'],
    enabled: enabled && bootReady,
    meta: persistedRelations('core_crm.gmail_provider', 'core_crm.gmail_mailbox'),
    ...commonQueryProps,
    queryFn: async () => {
      const [providerResult, mailboxResult] = await Promise.all([
        supabaseCoreClient
          .from('gmail_provider')
          .select('*')
          .eq('auth_type', 'workspace_delegation')
          .limit(1)
          .maybeSingle(),
        supabaseCoreClient
          .from('gmail_mailbox')
          .select('*')
          .order('email', { ascending: true }),
      ]);

      if (providerResult.error) throw providerResult.error;
      if (mailboxResult.error) throw mailboxResult.error;

      const mailboxes = (mailboxResult.data || []) as GmailMailbox[];
      return {
        provider: (providerResult.data || null) as GmailProvider | null,
        mailboxes,
        mailboxesByEmail: Object.fromEntries(mailboxes.map((mailbox) => [mailbox.email, mailbox])),
      };
    },
  });
};
